import { Provider as ReduxProvider } from 'react-redux';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AnalyticsProvider } from '@johnlewispartnership/wtr-website-analytics';
import { getAuthenticationProvider } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { AuthorizationServiceClient } from '@johnlewispartnership/wtr-website-authentication/dist/service';
import {
  ExperimentsContextType,
  FragmentExperimentsProvider,
} from '@johnlewispartnership/wtr-website-decisioner-client/express';
import { TrackedExperimentsProvider } from 'contexts/TrackedExperiments';
import { getAnalytics } from 'analytics';
import getPreloadedData from 'utils/dataPreloader/getPreloadedData';
import { SiteHeaderWrapper } from 'components/SiteHeaderWrapper';
import { PreloadedDataProvider } from 'contexts/PreloadedData';
import tokenServiceClient from 'services/token';
import config from 'config';
import { ShoppingContextProvider } from 'contexts/ShoppingContext';
import { TrolleyProvider } from 'contexts/Trolley';
import ReturnToAdmin from 'components/ReturnToAdmin';
import { CookieConsentProvider } from 'contexts/CookieConsent';
import { DeliveryPassProvider } from 'contexts/DeliveryPass';
import { getCookiePreferences } from 'utils/cookie';
import { SlotProvider } from 'contexts/Slot';
import { MenuExperienceFragmentsProvider } from 'contexts/MenuExperienceFragments';
import { CustomerOrderContextProvider } from 'contexts/CustomerOrder';
import { getApiResponseHandler } from 'client/utils/logging';
import { cookies } from 'client/utils/cookies';
import { headerPath } from 'server/controller/react/headerController/constants';
import { CommonModalProvider } from 'components/CommonModal/context';
import CommonModal from 'components/CommonModal';
import { store } from 'client/store';
import fragmentConfig from 'constants/fragment-config';
import { UserTracking } from 'components/UserTracking';

const preloadedData = getPreloadedData('header');

const authorizationServiceClient = new AuthorizationServiceClient(
  config.services.authorizationServer.url,
);

const AuthenticationProvider = getAuthenticationProvider({
  tokenServiceClient,
  tokenServiceBasePath: config.services.tokenClient.path,
  authorizationServiceClient,
});

const handleAuthApiResponses = getApiResponseHandler(headerPath);

const renderApp = () => {
  const rootElement = createRoot(document.getElementById('common-header-root')!, {
    identifierPrefix: `${fragmentConfig.header.identifier}-`,
  });

  // TODO hydrate store for single page app navigation here
  rootElement.render(
    <CookieConsentProvider cookieConsent={getCookiePreferences()}>
      <AuthenticationProvider
        initialState={{
          hasTokenSession: cookies.get('X-Has-Token-Session', { doNotParse: true }) === 'true',
        }}
        onApiResponse={handleAuthApiResponses}
      >
        <BrowserRouter>
          <ShoppingContextProvider>
            <SlotProvider>
              {/* TODO: TrolleyProvider should/could pick up server-side rendered initial state here */}
              <TrolleyProvider>
                <DeliveryPassProvider>
                  <CustomerOrderContextProvider>
                    <ReduxProvider store={store}>
                      <AnalyticsProvider
                        // eslint-disable-next-line no-underscore-dangle
                        initialEvents={window.__WTR_COMMON__?.header?.analytics}
                        analytics={getAnalytics()}
                      >
                        <FragmentExperimentsProvider
                          experimentsContext={
                            preloadedData?.experimentsContext as ExperimentsContextType
                          }
                        >
                          <TrackedExperimentsProvider>
                            <PreloadedDataProvider preloadedData={preloadedData}>
                              <CommonModalProvider>
                                <MenuExperienceFragmentsProvider>
                                  <ReturnToAdmin />
                                  <SiteHeaderWrapper />
                                  <UserTracking />
                                </MenuExperienceFragmentsProvider>
                                <CommonModal />
                              </CommonModalProvider>
                            </PreloadedDataProvider>
                          </TrackedExperimentsProvider>
                        </FragmentExperimentsProvider>
                      </AnalyticsProvider>
                    </ReduxProvider>
                  </CustomerOrderContextProvider>
                </DeliveryPassProvider>
              </TrolleyProvider>
            </SlotProvider>
          </ShoppingContextProvider>
        </BrowserRouter>
      </AuthenticationProvider>
    </CookieConsentProvider>,
  );
};

renderApp();
